/**
 * App-level preferences (§9.2), persisted per device and applied as data attributes on <html>
 * so CSS tokens can react (`data-motion`, `data-contrast`, `data-text-size`).
 */
import { create } from 'zustand';
import { useMediaQuery } from '../ui/focus';

export type MotionSetting = 'system' | 'reduce' | 'full';
export type TextSize = 'system' | 'large';

export interface AppSettings {
  motion: MotionSetting;
  highContrast: boolean;
  textSize: TextSize;
  haptics: boolean;
  keyboardHints: boolean;
}

interface SettingsState extends AppSettings {
  update(patch: Partial<AppSettings>): void;
}

const KEY = 'seven-dawns:settings:v1';

export const DEFAULT_SETTINGS: AppSettings = {
  motion: 'system',
  highContrast: false,
  textSize: 'system',
  haptics: true,
  keyboardHints: true,
};

function load(): AppSettings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<AppSettings>) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function applySettingsToDocument(s: AppSettings): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (s.motion === 'system') delete root.dataset.motion;
  else root.dataset.motion = s.motion;
  if (s.highContrast) root.dataset.contrast = 'high';
  else delete root.dataset.contrast;
  if (s.textSize === 'large') root.dataset.textSize = 'large';
  else delete root.dataset.textSize;
}

export const useSettings = create<SettingsState>((set, get) => ({
  ...load(),
  update(patch) {
    set(patch);
    const { update: _u, ...rest } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify(rest));
    } catch {
      // Private mode: settings last for this session.
    }
    applySettingsToDocument(rest);
  },
}));

/** True when motion should be reduced (system preference unless overridden in Settings). */
export function useReducedMotion(): boolean {
  const system = useMediaQuery('(prefers-reduced-motion: reduce)');
  const motion = useSettings((s) => s.motion);
  return motion === 'reduce' || (motion === 'system' && system);
}

/** Short vibration for confirmations, if enabled and supported. */
export function haptic(pattern: number | number[] = 10): void {
  if (!useSettings.getState().haptics) return;
  // Chrome logs an intervention if vibrate() runs before the first user gesture.
  if (typeof navigator === 'undefined' || navigator.userActivation?.hasBeenActive === false) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Not supported.
  }
}
